import { computed, type ComputedRef } from 'vue';
import { useFormValidation } from '@/composables/useFormValidation';
import type { DriverFormData, DriverModeFlags } from './types';

type FormValidation = ReturnType<typeof useFormValidation>;

interface DriverFormProps extends DriverModeFlags {
  formData: DriverFormData;
}

function hasContactBasics(data: DriverFormData, v: FormValidation): boolean {
  return (
    data.fullName.trim() !== '' &&
    v.isValidEmail(data.email) &&
    v.isValidPhone(data.whatsapp) &&
    data.privacyConsent === true
  );
}

function hasAuthenticDemographics(data: DriverFormData, v: FormValidation): boolean {
  return (data.gender === 'M' || data.gender === 'F') && v.isAdult(data.birthDate);
}

function checkComplete(props: DriverFormProps, v: FormValidation): boolean {
  const demographics = hasAuthenticDemographics(props.formData, v);
  if (props.demographicsOnly) return demographics;

  const contact = hasContactBasics(props.formData, v);
  if (props.contactOnly) return props.requireDemographics ? contact && demographics : contact;

  return contact && demographics && v.isValidPostalCode(props.formData.postalCode);
}

function resolveTitle(props: DriverModeFlags): string {
  if (props.demographicsOnly) return 'Datos del conductor';
  if (props.contactOnly) return 'Datos de contacto';
  return 'Información del conductor';
}

function resolveStepTwoLabel(props: DriverModeFlags): string {
  if (props.demographicsOnly) return 'Conductor';
  return props.contactOnly ? 'Contacto' : 'Conductor';
}

export function useDriverFormState(props: DriverFormProps): {
  showDemographicsFields: ComputedRef<boolean>;
  formTitle: ComputedRef<string>;
  stepTwoLabel: ComputedRef<string>;
  isComplete: ComputedRef<boolean>;
} {
  const validation = useFormValidation();

  return {
    showDemographicsFields: computed(
      () => props.demographicsOnly || props.requireDemographics || !props.contactOnly,
    ),
    formTitle: computed(() => resolveTitle(props)),
    stepTwoLabel: computed(() => resolveStepTwoLabel(props)),
    isComplete: computed(() => checkComplete(props, validation)),
  };
}
